import type { ConnectorSide } from "./canvas-object";
import {
  CONNECTOR_DEFAULT_END_CAP,
  CONNECTOR_DEFAULT_LINE_PATTERN,
  CONNECTOR_DEFAULT_LINE_TYPE,
  CONNECTOR_DEFAULT_START_CAP,
  CONNECTOR_ROUTE_CONSTRAINTS_MAX,
  type ConnectorCap,
  type ConnectorChannelAxis,
  type ConnectorLinePattern,
  type ConnectorLineType,
  type ConnectorRoute,
  type ConnectorRouteConstraint,
} from "./connector-appearance";

// コネクタ（接続矢印）の幾何計算。図形と図形を直交折れ線で結ぶための純粋関数群。
// クライアント描画（connector-object.tsx / オーバーレイ）と PDF / サムネイル出力に加え、
// サーバ側の参照整合（保存 bbox の再計算）でも共用するため domain に置く。
//
// 経路の改善と編集の追加後は、経路・描画命令（直線・角丸の折れ線・曲線）・両端の形状・外接矩形・
// 経路編集のハンドルの区間もここで求める。経路の正本はこのモジュールだけが持ち、描画面ごとに
// 別の経路計算を持たない。
//
// 自動経路は、接続する 2 図形だけを障害物とする有限の直交グラフ（候補座標の格子）上で、
// 「図形の内部を通らない → 折れの数が少ない（ただし、端の形状を置く最後の区間が形状の長さに足りない
// 窮屈な経路は折れ 2 つ分より重く扱う）→ 端点の助走（スタブ）が短すぎない → 距離が短い」の順に
// 費用を比べる最短路探索で求める。端点の外向き方向から出て、終点へは内向きに入り、直前の区間を
// 逆走しない。同じ入力は常に同じ経路になる（費用が同じなら探索の登録順で決まる）。

export interface ConnectorFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /**
   * 図形の回転角（度・時計回り）。未指定は 0（非回転）。
   * アンカー位置・外向き方向・外接矩形を、図形中心まわりに回転させて算出するために使う。
   */
  readonly rotation?: number | undefined;
}

export interface ConnectorPoint {
  readonly x: number;
  readonly y: number;
}

// 端点から外向きに伸ばす助走（スタブ）の長さ。矢印が図形へ垂直に出入りするように見せる。
// 狭い隙間では、経路が隙間の中に収まるよう短くする（下限は CONNECTOR_MIN_STUB_PX）。
export const CONNECTOR_STUB_PX = 24;

/** 端点から最初の折れまでの最小長（px）。これより短い助走は、端点で横へ折れて見えるため作らない。 */
export const CONNECTOR_MIN_STUB_PX = 4;

// 迂回ルート（同方向・背中合わせ・直交干渉）で図形の外側に確保するクリアランス（px）。
const DETOUR_MARGIN_PX = CONNECTOR_STUB_PX;

// コネクタ保存 bbox に与える余白（px）。矢じり・線幅の張り出しを含め、かつ水平/垂直コネクタで
// w/h が 0 になって範囲選択・他参加者選択枠が潰れるのを防ぐ。描画は端点から都度計算するため、
// この保存 bbox は AABB を参照する箇所（範囲選択・ミニマップ・他参加者選択枠・カリング）専用。
// （保存枠は、この余白を足したガイドの外接矩形と、線幅・両端の形状・曲線のふくらみを含む描画の
//  外接矩形の両方を包含する。connectorStoredFrame を参照。）
export const CONNECTOR_BBOX_PAD_PX = 12;

/** 角丸の折れ線（elbowed）の角の半径の上限（px）。隣り合う区間の長さの半分を超えない。 */
export const CONNECTOR_ELBOW_RADIUS_PX = 10;

/** 線の太さが未指定のときに幾何計算で使う値（クライアントの既定の太さと同じ 2px）。 */
export const CONNECTOR_DEFAULT_STROKE_WIDTH_PX = 2;

/** 端の形状の基準の大きさ（px）。従来の矢じりの長さ 12px を保ち、太い線では線幅の 3 倍まで大きくする。 */
export const CONNECTOR_CAP_BASE_PX = 12;

/** 1 回の経路探索で取り出す状態の上限。候補座標は有限のため通常はこの数よりずっと少ない。 */
export const CONNECTOR_ROUTE_MAX_SEARCH_STATES = 4096;

// 端点が同一座標に潰れたときに連続点を間引くための許容誤差。
const EPSILON = 0.01;

// 図形の縁へ線が重ならないよう、端点以外の区間で図形の内部とみなす外側のゆとり（px）。
const EDGE_CLEARANCE_PX = 0.5;

// 経路の費用の重み。図形の貫通（避けられないときだけ許す）> 折れ > 助走の不足 > 距離 の順に効かせる
// （端の形状の窮屈さ WEIGHT_CAP_CRAMPED は折れ 2 つ分より重い）。
const WEIGHT_CROSSING = 1_000_000_000;
const WEIGHT_BEND = 1_000_000;
const WEIGHT_STUB_SHORTFALL = 1_000;
// 両端の助走の釣り合い（1/長さ の和）の重み。同じ折れ数・距離の候補では、助走が両端で釣り合う
// （Z 字の中央の縦線が隙間の中央に来る）経路を選ぶ。
const WEIGHT_STUB_BALANCE = 50;
// 中間の区間が「隙間の中央」以外の候補座標を通るときの小さな費用。同じ費用の迂回の中から、
// 2 図形の隙間の中央を通る経路を選ぶための決定的な順位付けで、距離の比較を覆さない大きさにする。
const WEIGHT_OFF_CENTER = 0.5;
// 端の形状（矢じりなど）を置く端点で、折れの後の最後の区間が形状の長さより短い（形状が角に
// かぶって窮屈に見える）ことの費用。折れ 2 つぶんより重くし、形状を置けない短い助走の Z 字より、
// 助走を確保して外側を回る経路（折れが 2 つ多い）を選ぶ。1 本の直線で結ぶ経路には課さない。
const WEIGHT_CAP_CRAMPED = WEIGHT_BEND * 2 + WEIGHT_STUB_SHORTFALL * CONNECTOR_STUB_PX;
// 端の形状の長さに足す、角までのゆとり（px）。
const CAP_CLEARANCE_MARGIN_PX = 4;

/** 点を中心 (cx, cy) まわりに deg 度（時計回り・画面 y 下向き座標系）回転する。 */
function rotatePoint(p: ConnectorPoint, cx: number, cy: number, deg: number): ConnectorPoint {
  if (!deg) return p;
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - cx;
  const dy = p.y - cy;
  // y 下向き座標系では [[cos,-sin],[sin,cos]] が見た目の時計回り回転になる（CSS rotate と一致）。
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/** フレーム中心（ステージ座標）。回転の原点に使う。 */
function frameCenter(frame: ConnectorFrame): ConnectorPoint {
  return { x: frame.x + frame.w / 2, y: frame.y + frame.h / 2 };
}

/**
 * オブジェクト外接矩形の指定辺の中点（接続アンカー）をステージ座標で返す。
 * frame.rotation があれば、図形中心まわりに回転させた実際の辺中点を返す。
 */
export function sideAnchor(frame: ConnectorFrame, side: ConnectorSide): ConnectorPoint {
  const base = baseSideAnchor(frame, side);
  if (!frame.rotation) return base;
  const c = frameCenter(frame);
  return rotatePoint(base, c.x, c.y, frame.rotation);
}

/** 非回転前提の辺中点。 */
function baseSideAnchor(frame: ConnectorFrame, side: ConnectorSide): ConnectorPoint {
  switch (side) {
    case "top":
      return { x: frame.x + frame.w / 2, y: frame.y };
    case "bottom":
      return { x: frame.x + frame.w / 2, y: frame.y + frame.h };
    case "left":
      return { x: frame.x, y: frame.y + frame.h / 2 };
    case "right":
      return { x: frame.x + frame.w, y: frame.y + frame.h / 2 };
  }
}

/**
 * 指定辺の外向き単位ベクトル（非回転）を返す（top は上向き= y- 方向）。
 */
export function sideDirection(side: ConnectorSide): ConnectorPoint {
  switch (side) {
    case "top":
      return { x: 0, y: -1 };
    case "bottom":
      return { x: 0, y: 1 };
    case "left":
      return { x: -1, y: 0 };
    case "right":
      return { x: 1, y: 0 };
  }
}

/**
 * 回転を考慮した外向き単位ベクトルを返す。接続ドットの外向きオフセットや矢印アイコンの
 * 向き決定に使う（UI 表示用。orthogonal ルーティングには axis 吸着した {@link exitAxis} を使う）。
 */
export function sideUnitVector(side: ConnectorSide, rotationDeg = 0): ConnectorPoint {
  const base = sideDirection(side);
  if (!rotationDeg) return base;
  return rotatePoint(base, 0, 0, rotationDeg);
}

/**
 * 回転後の外向き方向を 4 軸（上下左右）のうち最も近い軸へ吸着した単位ベクトルを返す。
 * 直交折れ線ルーティングは軸平行のスタブ／中継線を前提とするため、回転図形でも軸へ丸めて
 * 経路の直交性を保つ（非回転では恒等で従来挙動と一致する）。
 * 経路の改善後は、回転図形では辺の法線方向へ短く出た先（斜めの助走）から、この軸の向きで
 * 直交経路を始める。
 */
function exitAxis(side: ConnectorSide, rotationDeg = 0): ConnectorPoint {
  const v = sideUnitVector(side, rotationDeg);
  return Math.abs(v.x) >= Math.abs(v.y)
    ? { x: v.x >= 0 ? 1 : -1, y: 0 }
    : { x: 0, y: v.y >= 0 ? 1 : -1 };
}

/**
 * フレームの軸平行外接矩形（AABB）を返す。回転がある場合は 4 隅を回転させた包含矩形を返す。
 * 迂回ルートで図形を避けるための障害物矩形として使う。
 * 経路の改善後は、迂回ルートの候補座標（図形の外側のチャネル）を決めるために使う（貫通の判定は
 * 実際の回転矩形で行う）。
 */
export function frameAABB(frame: ConnectorFrame): ConnectorFrame {
  if (!frame.rotation) return { x: frame.x, y: frame.y, w: frame.w, h: frame.h };
  const c = frameCenter(frame);
  const corners = [
    { x: frame.x, y: frame.y },
    { x: frame.x + frame.w, y: frame.y },
    { x: frame.x + frame.w, y: frame.y + frame.h },
    { x: frame.x, y: frame.y + frame.h },
  ].map((p) => rotatePoint(p, c.x, c.y, frame.rotation as number));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

/**
 * 指定点に最も近い辺を返す。接続先のスナップ辺の決定に使う。
 * 4 辺のアンカーのうち、点との距離が最小の辺を選ぶ（回転を考慮した実アンカー位置で比較する）。
 */
export function nearestSide(frame: ConnectorFrame, point: ConnectorPoint): ConnectorSide {
  const sides: readonly ConnectorSide[] = ["top", "right", "bottom", "left"];
  let best: ConnectorSide = "top";
  let bestDist = Infinity;
  for (const side of sides) {
    const anchor = sideAnchor(frame, side);
    const dist = (anchor.x - point.x) ** 2 + (anchor.y - point.y) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = side;
    }
  }
  return best;
}

/**
 * 当たり判定用に回転の三角関数を前計算した図形フレーム。経路探索では同じ図形へ何千回も判定するため、
 * 判定のたびに三角関数を計算しない。
 */
interface PreparedFrame {
  readonly frame: ConnectorFrame;
  readonly rotated: boolean;
  readonly cx: number;
  readonly cy: number;
  /** -rotation の余弦・正弦（ステージ座標 → 局所座標の変換に使う）。 */
  readonly cos: number;
  readonly sin: number;
}

function prepareFrame(frame: ConnectorFrame): PreparedFrame {
  const rotation = frame.rotation ?? 0;
  const radians = (-rotation * Math.PI) / 180;
  return {
    frame,
    rotated: rotation !== 0,
    cx: frame.x + frame.w / 2,
    cy: frame.y + frame.h / 2,
    cos: Math.cos(radians),
    sin: Math.sin(radians),
  };
}

/** ステージ座標の点を、図形の局所（未回転）座標へ変換する（中心まわりに -rotation 回転）。 */
function toLocalPrepared(p: ConnectorPoint, prepared: PreparedFrame): ConnectorPoint {
  if (!prepared.rotated) return p;
  const dx = p.x - prepared.cx;
  const dy = p.y - prepared.cy;
  return {
    x: prepared.cx + dx * prepared.cos - dy * prepared.sin,
    y: prepared.cy + dx * prepared.sin + dy * prepared.cos,
  };
}

/**
 * 線分が（回転を考慮した）図形フレームの内部を通過するか。Liang-Barsky を局所座標で行う。
 * 経路の改善後は、図形フレームを `inflate` だけ外側へ広げた矩形の内部を通過するかを判定する
 * （縁の上を通るだけの線分は内部とみなさない）。
 */
function segmentHitsFrame(
  p: ConnectorPoint,
  q: ConnectorPoint,
  target: ConnectorFrame | PreparedFrame,
  inflate: number
): boolean {
  const prepared = "cos" in target ? target : prepareFrame(target);
  const frame = prepared.frame;
  // 図形の局所（未回転）座標へ変換してから軸平行矩形との交差を解く。
  const a = toLocalPrepared(p, prepared);
  const b = toLocalPrepared(q, prepared);
  const x0 = frame.x - inflate + EPSILON;
  const x1 = frame.x + frame.w + inflate - EPSILON;
  const y0 = frame.y - inflate + EPSILON;
  const y1 = frame.y + frame.h + inflate - EPSILON;
  if (x1 <= x0 || y1 <= y0) return false;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  // Liang-Barsky。p:勾配係数, qv:境界までの余裕。
  // （ここでは線分の始点の引数 p と区別するため、勾配係数を pp と書く。）
  const clip = (pp: number, qv: number): boolean => {
    if (Math.abs(pp) < 1e-12) return qv >= 0;
    const r = qv / pp;
    if (pp < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  if (!clip(-dx, a.x - x0)) return false;
  if (!clip(dx, x1 - a.x)) return false;
  if (!clip(-dy, a.y - y0)) return false;
  if (!clip(dy, y1 - a.y)) return false;
  return t1 - t0 > 1e-9;
}

/** 経路探索の障害物（接続する 2 図形）。index 0 が始点側、1 が終点側。 */
interface RouteObstacle {
  readonly frame: PreparedFrame;
  readonly box: ConnectorFrame;
}

/**
 * 区間が障害物の内部を通る数を返す。端点の図形から出入りする区間（touchesStart / touchesEnd）は、
 * その図形の縁から出るため、その図形だけゆとりを付けずに判定する。
 */
function countSegmentHits(
  p: ConnectorPoint,
  q: ConnectorPoint,
  obstacles: readonly RouteObstacle[],
  touchesStart: boolean,
  touchesEnd: boolean
): number {
  let hits = 0;
  for (let index = 0; index < obstacles.length; index += 1) {
    const obstacle = obstacles[index]!;
    const own = (index === 0 && touchesStart) || (index === 1 && touchesEnd);
    if (segmentHitsFrame(p, q, obstacle.frame, own ? 0 : EDGE_CLEARANCE_PX)) hits += 1;
  }
  return hits;
}

// --- 直交経路の探索 -------------------------------------------------------------

/** 進行方向。0: +x、1: -x、2: +y、3: -y。 */
type Dir = 0 | 1 | 2 | 3;

const DIR_VECTORS: readonly ConnectorPoint[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

function dirOfAxisVector(v: ConnectorPoint): Dir {
  if (Math.abs(v.x) >= Math.abs(v.y)) return v.x >= 0 ? 0 : 1;
  return v.y >= 0 ? 2 : 3;
}

function isHorizontalDir(d: Dir): boolean {
  return d <= 1;
}

function oppositeDir(d: Dir): Dir {
  return (d ^ 1) as Dir;
}

function perpendicularDirs(d: Dir): readonly [Dir, Dir] {
  return isHorizontalDir(d) ? [2, 3] : [0, 1];
}

/**
 * 直交経路の一端。始点では `dir` が出ていく向き、終点では `dir` が外向き（到着はその逆向き）。
 * `preferredStub` は助走の目安の長さ（足りない分に費用を課す）、`minStub` は助走の最小長。
 * `capClearance` は、折れの後の最後の区間に欲しい長さ（端の形状の長さ＋ゆとり。形状が無ければ 0）。
 */
interface OrthoRouteEnd {
  readonly point: ConnectorPoint;
  readonly dir: Dir;
  readonly preferredStub: number;
  readonly minStub: number;
  readonly capClearance: number;
}

/** 候補座標 1 つ。`centered` は 2 図形の隙間の中央・端点の中点（同費用のとき優先する）。 */
interface Candidate {
  readonly value: number;
  readonly centered: boolean;
}

/**
 * 候補座標を昇順・重複なしにそろえる（近い値は先に登録した値＝端点の座標を残す）。点の重複を除く
 * 許容誤差（EPSILON）より近い候補を同じ座標にまとめ、経路の正規化で区間が消えて逆走が生じないようにする。
 */
function normalizeCandidates(values: readonly Candidate[]): Candidate[] {
  const kept: Candidate[] = [];
  for (const candidate of values) {
    if (!Number.isFinite(candidate.value)) continue;
    const existingIndex = kept.findIndex(
      (other) => Math.abs(other.value - candidate.value) < EPSILON
    );
    if (existingIndex >= 0) {
      const existing = kept[existingIndex]!;
      if (candidate.centered && !existing.centered) {
        kept[existingIndex] = { value: existing.value, centered: true };
      }
      continue;
    }
    kept.push(candidate);
  }
  return kept.sort((left, right) => left.value - right.value);
}

function buildAxisCandidates(
  start: OrthoRouteEnd,
  end: OrthoRouteEnd,
  obstacles: readonly RouteObstacle[],
  axis: "x" | "y"
): Candidate[] {
  const pick = (p: ConnectorPoint): number => (axis === "x" ? p.x : p.y);
  const values: Candidate[] = [
    { value: pick(start.point), centered: false },
    { value: pick(end.point), centered: false },
    { value: (pick(start.point) + pick(end.point)) / 2, centered: true },
  ];
  // 端点の助走の目安の位置（外向き方向へ CONNECTOR_STUB_PX）。軸平行の図形では図形の外側の
  // チャネルと一致するが、回転図形の斜めの助走の先から始める場合に、前方の候補を必ず用意する。
  for (const end_ of [start, end]) {
    const vector = DIR_VECTORS[end_.dir]!;
    const component = axis === "x" ? vector.x : vector.y;
    if (component !== 0) {
      values.push({ value: pick(end_.point) + component * CONNECTOR_STUB_PX, centered: false });
    }
  }
  const boxes = obstacles.map((obstacle) => obstacle.box);
  for (const box of boxes) {
    const min = axis === "x" ? box.x : box.y;
    const max = axis === "x" ? box.x + box.w : box.y + box.h;
    values.push({ value: min - DETOUR_MARGIN_PX, centered: false });
    values.push({ value: max + DETOUR_MARGIN_PX, centered: false });
  }
  const [first, second] = boxes;
  if (first && second) {
    const firstMin = axis === "x" ? first.x : first.y;
    const firstMax = axis === "x" ? first.x + first.w : first.y + first.h;
    const secondMin = axis === "x" ? second.x : second.y;
    const secondMax = axis === "x" ? second.x + second.w : second.y + second.h;
    // 2 図形の隙間の中央。隙間が狭いときに、両端の助走を短くした Z 字・コ字で隙間の中を通す。
    if (firstMax < secondMin) values.push({ value: (firstMax + secondMin) / 2, centered: true });
    if (secondMax < firstMin) values.push({ value: (secondMax + firstMin) / 2, centered: true });
  }
  return normalizeCandidates(values);
}

/** 最小ヒープ（費用・登録順で比較する）。 */
class SearchHeap {
  private readonly costs: number[] = [];
  private readonly orders: number[] = [];
  private readonly states: number[] = [];

  get size(): number {
    return this.states.length;
  }

  push(cost: number, order: number, state: number): void {
    this.costs.push(cost);
    this.orders.push(order);
    this.states.push(state);
    let index = this.states.length - 1;
    while (index > 0) {
      const parent = (index - 1) >> 1;
      if (!this.less(index, parent)) break;
      this.swap(index, parent);
      index = parent;
    }
  }

  pop(): { readonly cost: number; readonly state: number } | null {
    const count = this.states.length;
    if (count === 0) return null;
    const cost = this.costs[0]!;
    const state = this.states[0]!;
    const lastCost = this.costs.pop()!;
    const lastOrder = this.orders.pop()!;
    const lastState = this.states.pop()!;
    if (count > 1) {
      this.costs[0] = lastCost;
      this.orders[0] = lastOrder;
      this.states[0] = lastState;
      let index = 0;
      for (;;) {
        const left = index * 2 + 1;
        const right = left + 1;
        let smallest = index;
        if (left < this.states.length && this.less(left, smallest)) smallest = left;
        if (right < this.states.length && this.less(right, smallest)) smallest = right;
        if (smallest === index) break;
        this.swap(index, smallest);
        index = smallest;
      }
    }
    return { cost, state };
  }

  private less(left: number, right: number): boolean {
    const leftCost = this.costs[left]!;
    const rightCost = this.costs[right]!;
    if (leftCost !== rightCost) return leftCost < rightCost;
    return this.orders[left]! < this.orders[right]!;
  }

  private swap(left: number, right: number): void {
    const cost = this.costs[left]!;
    this.costs[left] = this.costs[right]!;
    this.costs[right] = cost;
    const order = this.orders[left]!;
    this.orders[left] = this.orders[right]!;
    this.orders[right] = order;
    const state = this.states[left]!;
    this.states[left] = this.states[right]!;
    this.states[right] = state;
  }
}

/**
 * 候補座標の格子上で、始点から終点までの直交経路（折れ点の列）を探す。
 *
 * 状態は「格子点 × 到着した向き」。1 回の移動は 1 本の直線区間で、移動のたびに 90° 折れる
 * （直前の区間の逆走は状態遷移として存在しない）。最初の区間は始点の外向き方向、最後の区間は
 * 終点へ内向きの方向に限る。`allowCrossing` が false のときは図形の内部を通る区間を作らず、
 * true のときは通過に大きな費用を課して、避けられない貫通が最も少ない経路を返す。
 */
function searchOrthogonalRoute(
  start: OrthoRouteEnd,
  end: OrthoRouteEnd,
  obstacles: readonly RouteObstacle[],
  allowCrossing: boolean
): ConnectorPoint[] | null {
  const xs = buildAxisCandidates(start, end, obstacles, "x");
  const ys = buildAxisCandidates(start, end, obstacles, "y");
  const nx = xs.length;
  const ny = ys.length;
  // 候補は EPSILON より近い値を 1 つにまとめているため、端点の座標も同じ許容誤差で最も近い候補を探す
  // （まとめる側より狭い許容誤差で探すと、始点の座標にまとめられた終点の座標が見つからず、経路を探せない）。
  const indexOf = (candidates: readonly Candidate[], value: number): number => {
    let found = -1;
    let foundDistance = EPSILON;
    for (const [index, candidate] of candidates.entries()) {
      const distance = Math.abs(candidate.value - value);
      if (distance < foundDistance) {
        found = index;
        foundDistance = distance;
      }
    }
    return found;
  };
  const startIx = indexOf(xs, start.point.x);
  const startIy = indexOf(ys, start.point.y);
  const endIx = indexOf(xs, end.point.x);
  const endIy = indexOf(ys, end.point.y);
  if (startIx < 0 || startIy < 0 || endIx < 0 || endIy < 0) return null;
  const endNode = endIx * ny + endIy;
  const nodePoint = (node: number): ConnectorPoint => {
    const iy = node % ny;
    const ix = (node - iy) / ny;
    return { x: xs[ix]!.value, y: ys[iy]!.value };
  };
  const stateCount = nx * ny * 4;
  const dist = new Float64Array(stateCount).fill(Infinity);
  const prev = new Int32Array(stateCount).fill(-2);
  const heap = new SearchHeap();
  let order = 0;
  const arrival = oppositeDir(end.dir);
  let bestGoalCost = Infinity;
  // 終点へ入る直前の状態（-1 は始点から直接入る直線）。-2 は未到達。
  let bestGoalPrev = -2;

  const relax = (state: number, cost: number, from: number): void => {
    if (cost >= dist[state]!) return;
    dist[state] = cost;
    prev[state] = from;
    heap.push(cost, order, state);
    order += 1;
  };

  // (ix, iy) から dir の向きへ格子点を 1 つずつ進み、到達できる点ごとに visit を呼ぶ。
  // visit が false を返すか、図形を貫通する区間（allowCrossing が false のとき）で止まる。
  const walk = (
    ix: number,
    iy: number,
    dir: Dir,
    touchesStart: boolean,
    visit: (node: number, length: number, hits: number, offCenter: boolean) => boolean
  ): void => {
    const vector = DIR_VECTORS[dir]!;
    let cx = ix;
    let cy = iy;
    let hits = 0;
    const origin = { x: xs[ix]!.value, y: ys[iy]!.value };
    for (;;) {
      const nextX = cx + vector.x;
      const nextY = cy + vector.y;
      if (nextX < 0 || nextX >= nx || nextY < 0 || nextY >= ny) return;
      const from = { x: xs[cx]!.value, y: ys[cy]!.value };
      const to = { x: xs[nextX]!.value, y: ys[nextY]!.value };
      const nextNode = nextX * ny + nextY;
      const stepHits = countSegmentHits(from, to, obstacles, touchesStart, nextNode === endNode);
      if (stepHits > 0 && !allowCrossing) return;
      hits += stepHits;
      cx = nextX;
      cy = nextY;
      const length = Math.abs(to.x - origin.x) + Math.abs(to.y - origin.y);
      // 区間の位置（水平移動なら y、垂直移動なら x）が隙間の中央でなければ、小さな費用を課す。
      const offCenter = isHorizontalDir(dir) ? !ys[iy]!.centered : !xs[ix]!.centered;
      if (!visit(nextNode, length, hits, offCenter)) return;
    }
  };

  const stubCost = (end_: OrthoRouteEnd, length: number): number =>
    WEIGHT_STUB_SHORTFALL * Math.max(0, end_.preferredStub - length) +
    (end_.preferredStub > 0 ? WEIGHT_STUB_BALANCE / Math.max(length, EPSILON) : 0);
  // 折れのある経路で、端の形状を置く端点の区間が形状の長さに足りない場合の費用。
  const crampedCost = (end_: OrthoRouteEnd, length: number): number =>
    length < end_.capClearance - EPSILON ? WEIGHT_CAP_CRAMPED : 0;

  // 最初の区間（始点の外向き方向。折れは数えない）。
  walk(startIx, startIy, start.dir, true, (node, length, hits) => {
    if (length < start.minStub) return true;
    const cost = length + stubCost(start, length) + hits * WEIGHT_CROSSING;
    if (node === endNode) {
      // 始点から終点へ直接入る直線（向かい合った端点が一直線に並ぶ場合）。形状は直線の上に置けるため、
      // 窮屈さの費用は課さない。
      if (start.dir === arrival && length >= end.minStub) {
        const total = cost + stubCost(end, length);
        if (total < bestGoalCost) {
          bestGoalCost = total;
          bestGoalPrev = -1;
        }
      }
      return false;
    }
    relax(node * 4 + start.dir, cost + crampedCost(start, length), -1);
    return true;
  });

  let popped = 0;
  for (;;) {
    const entry = heap.pop();
    if (!entry) break;
    if (entry.cost > dist[entry.state]!) continue;
    if (entry.cost >= bestGoalCost) break;
    popped += 1;
    if (popped > CONNECTOR_ROUTE_MAX_SEARCH_STATES) break;
    const state = entry.state;
    const dir = (state & 3) as Dir;
    const node = state >> 2;
    const iy = node % ny;
    const ix = (node - iy) / ny;
    for (const nextDir of perpendicularDirs(dir)) {
      walk(ix, iy, nextDir, false, (nextNode, length, hits, offCenter) => {
        const cost =
          entry.cost +
          length +
          WEIGHT_BEND +
          hits * WEIGHT_CROSSING +
          (offCenter ? WEIGHT_OFF_CENTER : 0);
        if (nextNode === endNode) {
          if (nextDir === arrival && length >= end.minStub) {
            const total =
              cost +
              stubCost(end, length) +
              crampedCost(end, length) -
              (offCenter ? WEIGHT_OFF_CENTER : 0);
            if (total < bestGoalCost) {
              bestGoalCost = total;
              bestGoalPrev = state;
            }
          }
          return false;
        }
        relax(nextNode * 4 + nextDir, cost, state);
        return true;
      });
    }
  }

  if (bestGoalPrev === -2) return null;
  const reversed: ConnectorPoint[] = [end.point];
  let cursor = bestGoalPrev;
  while (cursor >= 0) {
    reversed.push(nodePoint(cursor >> 2));
    cursor = prev[cursor]!;
  }
  reversed.push(start.point);
  return reversed.reverse();
}

/**
 * 探索で経路が見つからない場合の最終手段（通常は到達しない）。両端の助走の先を直交に結ぶ。
 */
function fallbackOrthogonalRoute(start: OrthoRouteEnd, end: OrthoRouteEnd): ConnectorPoint[] {
  const startVector = DIR_VECTORS[start.dir]!;
  const endVector = DIR_VECTORS[end.dir]!;
  const s1 = {
    x: start.point.x + startVector.x * CONNECTOR_STUB_PX,
    y: start.point.y + startVector.y * CONNECTOR_STUB_PX,
  };
  const e1 = {
    x: end.point.x + endVector.x * CONNECTOR_STUB_PX,
    y: end.point.y + endVector.y * CONNECTOR_STUB_PX,
  };
  const corner = isHorizontalDir(start.dir) ? { x: s1.x, y: e1.y } : { x: e1.x, y: s1.y };
  return [start.point, s1, corner, e1, end.point];
}

/**
 * 直交経路を求める（貫通なし → 貫通を最小化 → 最終手段の順）。`crossing` は、図形が重なっている
 * などで貫通を避けられなかった（貫通しない経路が見つからなかった）か。
 */
function routeOrthogonal(
  start: OrthoRouteEnd,
  end: OrthoRouteEnd,
  obstacles: readonly RouteObstacle[]
): { readonly points: ConnectorPoint[]; readonly crossing: boolean } {
  if (
    Math.abs(start.point.x - end.point.x) < EPSILON &&
    Math.abs(start.point.y - end.point.y) < EPSILON
  ) {
    return { points: [start.point, end.point], crossing: false };
  }
  const clean = searchOrthogonalRoute(start, end, obstacles, false);
  if (clean) return { points: clean, crossing: false };
  return {
    points:
      searchOrthogonalRoute(start, end, obstacles, true) ?? fallbackOrthogonalRoute(start, end),
    crossing: true,
  };
}

// --- 手動経路（チャネル）の復元 -----------------------------------------------------

/**
 * 経路を構成する直線 1 本（向きと位置）。`horizontal` は水平線（y = coord）、そうでなければ垂直線（x = coord）。
 * 手動経路は「始点の助走の線 → チャネル（縦横交互）→ 終点の助走の線」の並びで表す。
 */
interface RouteLine {
  readonly horizontal: boolean;
  readonly coord: number;
  /** チャネルの id。始点・終点の助走の線と、補った暗黙のチャネルでは undefined。 */
  readonly id?: string | undefined;
  /** 始点・終点の助走の線（位置を動かせない線）か。 */
  readonly fixed: boolean;
}

/** 表示中の経路のチャネル（手動経路へ変換するときの素材）。 */
export interface ConnectorChannel {
  readonly axis: ConnectorChannelAxis;
  /** 始点アンカーからの相対位置（canvas px）。 */
  readonly offset: number;
  /** 保存済みのチャネルの id。自動経路から求めたチャネル・補ったチャネルでは undefined。 */
  readonly id?: string | undefined;
}

/** 直線の並びから折れ点の列を求める（隣り合う直線は直交している前提）。 */
function intersectLines(lines: readonly RouteLine[]): ConnectorPoint[] {
  const points: ConnectorPoint[] = [];
  for (let index = 0; index < lines.length - 1; index += 1) {
    const current = lines[index]!;
    const next = lines[index + 1]!;
    points.push(
      current.horizontal ? { x: next.coord, y: current.coord } : { x: current.coord, y: next.coord }
    );
  }
  return points;
}

/**
 * 同じ向きの直線が 1 本おきに同じ位置にある（間の線上の区間が長さ 0 になる）とき、その 2 本を除いて
 * 並びを簡約する。端の助走の線（fixed）は除かず、簡約で動かせる線だけを取り除く。
 */
function simplifyLines(lines: readonly RouteLine[]): RouteLine[] {
  let current = [...lines];
  let changed = true;
  while (changed) {
    changed = false;
    for (let index = 1; index < current.length - 1; index += 1) {
      const before = current[index - 1]!;
      const after = current[index + 1]!;
      const middle = current[index]!;
      if (before.horizontal !== after.horizontal) continue;
      if (Math.abs(before.coord - after.coord) >= EPSILON) continue;
      if (middle.fixed) continue;
      // before と after が同じ線になり、middle 上の区間は長さ 0。middle と、固定でない側の線を除く。
      if (!after.fixed) {
        current = [...current.slice(0, index), ...current.slice(index + 2)];
      } else if (!before.fixed) {
        current = [...current.slice(0, index - 1), ...current.slice(index + 1)];
      } else {
        // 両端の助走の線が一直線に並び、間のチャネルが 1 本だけ（長さ 0）の場合は、そのチャネルを除く。
        current = [...current.slice(0, index), ...current.slice(index + 1)];
      }
      changed = true;
      break;
    }
  }
  return current;
}

/** 経路の両端（直交部分の始点・終点）。 */
interface OrthoEnds {
  /** 始点アンカー（手動経路の相対位置の基準）。 */
  readonly anchor: ConnectorPoint;
  readonly start: OrthoRouteEnd;
  readonly end: OrthoRouteEnd;
}

/** チャネルの並びから、両端の助走の線を含む直線の並びを作る（向きの交互が崩れていれば補う）。 */
function linesFromChannels(
  ends: OrthoEnds,
  channels: readonly ConnectorChannel[]
): { readonly lines: RouteLine[]; readonly inserted: boolean } {
  const startHorizontal = isHorizontalDir(ends.start.dir);
  const endHorizontal = isHorizontalDir(ends.end.dir);
  const startLine: RouteLine = {
    horizontal: startHorizontal,
    coord: startHorizontal ? ends.start.point.y : ends.start.point.x,
    fixed: true,
  };
  const endLine: RouteLine = {
    horizontal: endHorizontal,
    coord: endHorizontal ? ends.end.point.y : ends.end.point.x,
    fixed: true,
  };
  const middle: RouteLine[] = channels.map((channel) => ({
    horizontal: channel.axis === "y",
    coord: channel.axis === "y" ? ends.anchor.y + channel.offset : ends.anchor.x + channel.offset,
    id: channel.id,
    fixed: false,
  }));
  let inserted = false;
  const startVector = DIR_VECTORS[ends.start.dir]!;
  const endVector = DIR_VECTORS[ends.end.dir]!;
  // 最初のチャネルが始点の助走の線と同じ向きなら、助走の先に向きを変える暗黙のチャネルを補う。
  const first = middle[0];
  if (first && first.horizontal === startLine.horizontal) {
    middle.unshift({
      horizontal: !startLine.horizontal,
      coord: startLine.horizontal
        ? ends.start.point.x + startVector.x * CONNECTOR_STUB_PX
        : ends.start.point.y + startVector.y * CONNECTOR_STUB_PX,
      fixed: false,
    });
    inserted = true;
  }
  const last = middle[middle.length - 1];
  if (last && last.horizontal === endLine.horizontal) {
    middle.push({
      horizontal: !endLine.horizontal,
      coord: endLine.horizontal
        ? ends.end.point.x + endVector.x * CONNECTOR_STUB_PX
        : ends.end.point.y + endVector.y * CONNECTOR_STUB_PX,
      fixed: false,
    });
    inserted = true;
  }
  return { lines: [startLine, ...middle, endLine], inserted };
}

/**
 * 直線の並びが経路として成り立つか（両端の向き・助走・逆走・図形の貫通）。`ignoreCrossings` は、
 * 図形が重なっていて貫通を避けられない配置で、貫通以外の条件だけを確かめるときに使う。
 */
function isValidLineRoute(
  ends: OrthoEnds,
  lines: readonly RouteLine[],
  obstacles: readonly RouteObstacle[],
  ignoreCrossings = false
): boolean {
  if (lines.length < 2) return false;
  for (let index = 0; index < lines.length - 1; index += 1) {
    if (lines[index]!.horizontal === lines[index + 1]!.horizontal) return false;
  }
  const turns = intersectLines(lines);
  const points = [ends.start.point, ...turns, ends.end.point];
  const startVector = DIR_VECTORS[ends.start.dir]!;
  const endVector = DIR_VECTORS[ends.end.dir]!;
  const firstTurn = turns[0];
  const lastTurn = turns[turns.length - 1];
  if (!firstTurn || !lastTurn) return false;
  // 最初の折れは始点の前方、最後の折れは終点の前方（外向き側）に、最小の助走を空けて置く。
  const startAhead =
    (firstTurn.x - ends.start.point.x) * startVector.x +
    (firstTurn.y - ends.start.point.y) * startVector.y;
  const endAhead =
    (lastTurn.x - ends.end.point.x) * endVector.x + (lastTurn.y - ends.end.point.y) * endVector.y;
  if (startAhead < ends.start.minStub - EPSILON) return false;
  if (endAhead < ends.end.minStub - EPSILON) return false;
  // 長さ 0 の区間で前後が一直線になり、逆向きに重なる（逆走）配置を除く。
  const normalized = normalizePolyline(points);
  if (normalized === null) return false;
  if (ignoreCrossings) return true;
  for (let index = 0; index < normalized.length - 1; index += 1) {
    const p = normalized[index]!;
    const q = normalized[index + 1]!;
    const touchesStart = index === 0;
    const touchesEnd = index === normalized.length - 2;
    if (countSegmentHits(p, q, obstacles, touchesStart, touchesEnd) > 0) return false;
  }
  return true;
}

/**
 * 点列から重複点と一直線上の中間点を除く。逆向きに折り返す（180° の）点があれば null を返す。
 */
function normalizePolyline(points: readonly ConnectorPoint[]): ConnectorPoint[] | null {
  const deduped = dedupePoints(points);
  const result: ConnectorPoint[] = [];
  for (const point of deduped) {
    while (result.length >= 2) {
      const a = result[result.length - 2]!;
      const b = result[result.length - 1]!;
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const bcx = point.x - b.x;
      const bcy = point.y - b.y;
      const cross = abx * bcy - aby * bcx;
      const lengthProduct = Math.hypot(abx, aby) * Math.hypot(bcx, bcy);
      if (lengthProduct < EPSILON || Math.abs(cross) > 1e-9 * Math.max(1, lengthProduct)) break;
      const dot = abx * bcx + aby * bcy;
      if (dot < 0) return null;
      result.pop();
    }
    result.push(point);
  }
  return result;
}

/** 手動経路の復元結果。 */
interface ManualRouteResult {
  readonly points: ConnectorPoint[];
  readonly channels: ConnectorChannel[];
  /** 保存したチャネルをそのまま満たせず、表示のために位置を補正した（または補った）か。 */
  readonly adjusted: boolean;
}

function channelsFromLines(ends: OrthoEnds, lines: readonly RouteLine[]): ConnectorChannel[] {
  return lines
    .filter((line) => !line.fixed)
    .map((line) => ({
      axis: line.horizontal ? "y" : "x",
      offset: line.horizontal ? line.coord - ends.anchor.y : line.coord - ends.anchor.x,
      ...(line.id === undefined ? {} : { id: line.id }),
    }));
}

/**
 * 保存されたチャネルから手動経路を復元する。図形の移動などでチャネルをそのまま満たせない場合は、
 * 助走の不足・図形の貫通を起こしているチャネルだけを近くの外側へ移して表示する（保存値は変えない）。
 * 補正しても成り立たない場合は null を返し、呼び出し側は自動経路で表示する。
 * `ignoreCrossings` は、図形が重なっていて貫通を避けられない配置で、貫通以外の条件だけを確かめるときに使う。
 */
function reconstructManualRoute(
  ends: OrthoEnds,
  constraints: readonly ConnectorRouteConstraint[],
  obstacles: readonly RouteObstacle[],
  ignoreCrossings: boolean
): ManualRouteResult | null {
  if (constraints.length === 0) return null;
  const built = linesFromChannels(
    ends,
    constraints.map((constraint) => ({
      axis: constraint.axis,
      offset: constraint.offset,
      id: constraint.id,
    }))
  );
  let lines = simplifyLines(built.lines);
  let adjusted = built.inserted;
  if (!isValidLineRoute(ends, lines, obstacles, ignoreCrossings)) {
    const projected = projectLines(ends, lines, obstacles, ignoreCrossings);
    if (!projected) return null;
    lines = projected;
    adjusted = true;
  }
  const points = normalizePolyline([ends.start.point, ...intersectLines(lines), ends.end.point]);
  if (!points) return null;
  return { points, channels: channelsFromLines(ends, lines), adjusted };
}

/**
 * 成り立たないチャネルを近くの外側へ移す（有限回）。最初のチャネルは始点の前方へ、最後のチャネルは
 * 終点の前方へ、図形を貫通する区間を作るチャネルは、その図形の外側のチャネル位置へ移す。
 */
function projectLines(
  ends: OrthoEnds,
  lines: readonly RouteLine[],
  obstacles: readonly RouteObstacle[],
  ignoreCrossings: boolean
): RouteLine[] | null {
  let current = [...lines];
  const startVector = DIR_VECTORS[ends.start.dir]!;
  const endVector = DIR_VECTORS[ends.end.dir]!;
  for (let attempt = 0; attempt < current.length + 2; attempt += 1) {
    if (isValidLineRoute(ends, current, obstacles, ignoreCrossings)) return current;
    const turns = intersectLines(current);
    const firstTurn = turns[0];
    const lastTurn = turns[turns.length - 1];
    if (!firstTurn || !lastTurn) return null;
    const next = [...current];
    let moved = false;
    let movedFirst = false;
    // 始点の助走の不足: 最初のチャネルを始点の前方の助走の位置へ。
    const startAhead =
      (firstTurn.x - ends.start.point.x) * startVector.x +
      (firstTurn.y - ends.start.point.y) * startVector.y;
    const firstChannel = next[1];
    if (startAhead < ends.start.minStub - EPSILON && firstChannel && !firstChannel.fixed) {
      next[1] = {
        ...firstChannel,
        coord: firstChannel.horizontal
          ? ends.start.point.y + startVector.y * CONNECTOR_STUB_PX
          : ends.start.point.x + startVector.x * CONNECTOR_STUB_PX,
      };
      moved = true;
      movedFirst = true;
    }
    const endAhead =
      (lastTurn.x - ends.end.point.x) * endVector.x + (lastTurn.y - ends.end.point.y) * endVector.y;
    const lastChannelIndex = next.length - 2;
    const lastChannel = next[lastChannelIndex];
    // チャネルが 1 本だけ（最初と最後が同じ線）のときは、この回で始点側の補正に使っていなければ終点側を補正する。
    if (
      endAhead < ends.end.minStub - EPSILON &&
      lastChannel &&
      !lastChannel.fixed &&
      !(lastChannelIndex === 1 && movedFirst)
    ) {
      next[lastChannelIndex] = {
        ...lastChannel,
        coord: lastChannel.horizontal
          ? ends.end.point.y + endVector.y * CONNECTOR_STUB_PX
          : ends.end.point.x + endVector.x * CONNECTOR_STUB_PX,
      };
      moved = true;
    }
    if (!moved) {
      // 図形を貫通する区間のチャネルを、その図形の外側のチャネル位置（近い側）へ移す。
      const points = [ends.start.point, ...turns, ends.end.point];
      for (let index = 1; index < next.length - 1 && !moved; index += 1) {
        const line = next[index]!;
        if (line.fixed) continue;
        // 直線 index 上の区間は、折れ点 index - 1 から index まで（points では index から index + 1）。
        const p = points[index]!;
        const q = points[index + 1]!;
        for (const obstacle of obstacles) {
          if (!segmentHitsFrame(p, q, obstacle.frame, EDGE_CLEARANCE_PX)) continue;
          const min = line.horizontal ? obstacle.box.y : obstacle.box.x;
          const max = line.horizontal
            ? obstacle.box.y + obstacle.box.h
            : obstacle.box.x + obstacle.box.w;
          const below = min - DETOUR_MARGIN_PX;
          const above = max + DETOUR_MARGIN_PX;
          const coord =
            Math.abs(line.coord - below) <= Math.abs(line.coord - above) ? below : above;
          next[index] = { ...line, coord };
          moved = true;
          break;
        }
      }
    }
    if (!moved) return null;
    current = simplifyLines(next);
  }
  return isValidLineRoute(ends, current, obstacles, ignoreCrossings) ? current : null;
}

// --- 経路の組み立て -----------------------------------------------------------------

/** 経路計算の入力。見た目の項目は未指定なら既定値（角丸の折れ線・自動経路・実線・終点に開いた矢印）。 */
export interface ConnectorGeometryInput {
  readonly fromFrame: ConnectorFrame;
  readonly fromSide: ConnectorSide;
  readonly toFrame: ConnectorFrame;
  readonly toSide: ConnectorSide;
  readonly lineType?: ConnectorLineType | undefined;
  readonly linePattern?: ConnectorLinePattern | undefined;
  readonly route?: ConnectorRoute | undefined;
  readonly strokeWidth?: number | undefined;
  readonly startCap?: ConnectorCap | undefined;
  readonly endCap?: ConnectorCap | undefined;
}

/** 描画命令。Q は二次ベジェ（制御点 1 つ）。 */
export type ConnectorPathCommand =
  | { readonly kind: "M"; readonly to: ConnectorPoint }
  | { readonly kind: "L"; readonly to: ConnectorPoint }
  | { readonly kind: "Q"; readonly control: ConnectorPoint; readonly to: ConnectorPoint };

/** 経路編集のハンドルを置く区間の役割。 */
export type ConnectorSegmentRole = "start" | "middle" | "end" | "single";

/** 経路（ガイドの折れ線）の区間 1 つ。ハンドルの位置と、ドラッグで動かせる向きを表す。 */
export interface ConnectorGuideSegment {
  /** ガイドの点列での区間の先頭の添字。 */
  readonly index: number;
  readonly start: ConnectorPoint;
  readonly end: ConnectorPoint;
  /**
   * ドラッグで動かす座標軸。`x` は縦の区間（左右へ動かす）、`y` は横の区間（上下へ動かす）。
   * 斜めの区間（直線・回転図形の助走）は null で、ハンドルを出さない（直線は別扱い）。
   */
  readonly axis: ConnectorChannelAxis | null;
  readonly role: ConnectorSegmentRole;
  /** role が middle のとき、表示中のチャネルの添字。 */
  readonly channelIndex?: number | undefined;
  /** ハンドルを置く位置（描画した線の上）。 */
  readonly handlePoint: ConnectorPoint;
}

/** 端の形状の描画要素。線（strokes）は線と同じ色・太さ、塗り（fills）は線と同じ色で描く。 */
export interface ConnectorCapGeometry {
  readonly cap: ConnectorCap;
  /** 端点（接続アンカー）。 */
  readonly tip: ConnectorPoint;
  /** 端点へ向かう単位ベクトル（線の進行方向）。 */
  readonly direction: ConnectorPoint;
  readonly strokes: readonly (readonly ConnectorPoint[])[];
  readonly fills: readonly (readonly ConnectorPoint[])[];
  readonly circles: readonly {
    readonly center: ConnectorPoint;
    readonly radius: number;
    readonly filled: boolean;
  }[];
  /** 端点から線の内側へ向かう、形状が占める長さ。 */
  readonly extent: number;
  /** 線本体を端点から縮める長さ（形状の内側に線端が隠れる・中空の丸の内側に線を通さない）。 */
  readonly trim: number;
}

/** 経路と描画に必要な結果一式。 */
export interface ConnectorGeometry {
  readonly lineType: ConnectorLineType;
  readonly linePattern: ConnectorLinePattern;
  readonly strokeWidth: number;
  /** ガイドの折れ線（始点アンカー → 終点アンカー）。曲線ではこの折れ線を滑らかにして描く。 */
  readonly guide: readonly ConnectorPoint[];
  /** 線本体の描画命令（両端の形状のぶん縮めたもの）。 */
  readonly path: readonly ConnectorPathCommand[];
  /** 当たり判定用の描画命令（縮めない全体。破線でも連続した線として扱う）。 */
  readonly hitPath: readonly ConnectorPathCommand[];
  /** 破線の間隔（線幅基準の 4w 3w）。実線は null。 */
  readonly dashArray: readonly number[] | null;
  readonly startCap: ConnectorCapGeometry | null;
  readonly endCap: ConnectorCapGeometry | null;
  /** 経路編集のハンドルを置ける区間。 */
  readonly segments: readonly ConnectorGuideSegment[];
  /** 表示中の経路のチャネル（自動経路でも求める。手動経路へ変換するときの素材）。 */
  readonly channels: readonly ConnectorChannel[];
  /** 線・両端の形状・線幅を含む外接矩形（ステージ座標）。 */
  readonly bounds: ConnectorFrame;
  /** 手動経路を配置に合わせて補正して表示している（保存値をそのまま満たせない）か。 */
  readonly adjusted: boolean;
  /** 表示中の経路が手動経路か（補正で自動経路へ戻した場合は false）。 */
  readonly manual: boolean;
}

/**
 * 回転図形の辺から、辺の法線方向（斜め）へ出る助走の長さ。相手の図形にぶつからない長さに縮める。
 */
function obliqueStubLength(
  anchor: ConnectorPoint,
  normal: ConnectorPoint,
  other: ConnectorFrame
): number {
  const reach = (length: number): ConnectorPoint => ({
    x: anchor.x + normal.x * length,
    y: anchor.y + normal.y * length,
  });
  if (!segmentHitsFrame(anchor, reach(CONNECTOR_STUB_PX), other, EDGE_CLEARANCE_PX)) {
    return CONNECTOR_STUB_PX;
  }
  // ぶつからない最大の長さを二分探索で求め、その半分を助走にする（相手との隙間の中央で折れる）。
  let low = 0;
  let high = CONNECTOR_STUB_PX;
  for (let iteration = 0; iteration < 16; iteration += 1) {
    const middle = (low + high) / 2;
    if (segmentHitsFrame(anchor, reach(middle), other, EDGE_CLEARANCE_PX)) high = middle;
    else low = middle;
  }
  return Math.max(low / 2, EPSILON);
}

/** 経路の両端（アンカー・助走・直交部分の始点と終点）。 */
interface RouteEnds {
  readonly a: ConnectorPoint;
  readonly b: ConnectorPoint;
  /** 斜めの助走の先（回転図形のときだけアンカーと異なる）。 */
  readonly a1: ConnectorPoint;
  readonly b1: ConnectorPoint;
  readonly ortho: OrthoEnds;
  readonly obstacles: readonly RouteObstacle[];
}

function resolveRouteEnds(input: ConnectorGeometryInput): RouteEnds {
  const { fromFrame, fromSide, toFrame, toSide } = input;
  const a = sideAnchor(fromFrame, fromSide);
  const b = sideAnchor(toFrame, toSide);
  const aNormal = sideUnitVector(fromSide, fromFrame.rotation);
  const bNormal = sideUnitVector(toSide, toFrame.rotation);
  const aAxis = exitAxis(fromSide, fromFrame.rotation);
  const bAxis = exitAxis(toSide, toFrame.rotation);
  const aOblique = Math.abs(aNormal.x - aAxis.x) > 1e-9 || Math.abs(aNormal.y - aAxis.y) > 1e-9;
  const bOblique = Math.abs(bNormal.x - bAxis.x) > 1e-9 || Math.abs(bNormal.y - bAxis.y) > 1e-9;
  const aStub = aOblique ? obliqueStubLength(a, aNormal, toFrame) : 0;
  const bStub = bOblique ? obliqueStubLength(b, bNormal, fromFrame) : 0;
  const a1 = aOblique ? { x: a.x + aNormal.x * aStub, y: a.y + aNormal.y * aStub } : a;
  const b1 = bOblique ? { x: b.x + bNormal.x * bStub, y: b.y + bNormal.y * bStub } : b;
  const obstacles: RouteObstacle[] = [
    { frame: prepareFrame(fromFrame), box: frameAABB(fromFrame) },
    { frame: prepareFrame(toFrame), box: frameAABB(toFrame) },
  ];
  const strokeWidth = resolveStrokeWidth(input.strokeWidth);
  return {
    a,
    b,
    a1,
    b1,
    obstacles,
    ortho: {
      anchor: a,
      // 斜めの助走で図形から既に離れている端では、直交部分の助走の目安・形状のゆとりを求めない。
      start: {
        point: a1,
        dir: dirOfAxisVector(aAxis),
        preferredStub: aOblique ? 0 : CONNECTOR_STUB_PX,
        minStub: aOblique ? EPSILON : CONNECTOR_MIN_STUB_PX,
        capClearance: aOblique
          ? 0
          : capClearanceFor(input.startCap ?? CONNECTOR_DEFAULT_START_CAP, strokeWidth),
      },
      end: {
        point: b1,
        dir: dirOfAxisVector(bAxis),
        preferredStub: bOblique ? 0 : CONNECTOR_STUB_PX,
        minStub: bOblique ? EPSILON : CONNECTOR_MIN_STUB_PX,
        capClearance: bOblique
          ? 0
          : capClearanceFor(input.endCap ?? CONNECTOR_DEFAULT_END_CAP, strokeWidth),
      },
    },
  };
}

/** 線幅の入力を描画に使う値へそろえる（未指定・不正は既定の 2px）。 */
function resolveStrokeWidth(strokeWidth: number | undefined): number {
  return typeof strokeWidth === "number" && Number.isFinite(strokeWidth) && strokeWidth > 0
    ? strokeWidth
    : CONNECTOR_DEFAULT_STROKE_WIDTH_PX;
}

/** 端の形状を置く端点で、折れの後の最後の区間に欲しい長さ（形状の長さ＋ゆとり）。形状が無ければ 0。 */
function capClearanceFor(cap: ConnectorCap, strokeWidth: number): number {
  const geometry = buildConnectorCap(cap, { x: 0, y: 0 }, { x: 1, y: 0 }, strokeWidth);
  return geometry ? geometry.extent + CAP_CLEARANCE_MARGIN_PX : 0;
}

/** 直交部分の点列（a1 → … → b1）から、表示中のチャネルを求める（自動経路のため id は持たない）。 */
function channelsFromOrthoPoints(ends: OrthoEnds, points: readonly ConnectorPoint[]): ConnectorChannel[] {
  const channels: ConnectorChannel[] = [];
  // 区間 0 は始点の助走の線、最後の区間は終点の助走の線。その間がチャネル。
  for (let index = 1; index < points.length - 2; index += 1) {
    const p = points[index]!;
    const q = points[index + 1]!;
    const vertical = Math.abs(p.x - q.x) < EPSILON;
    channels.push({
      axis: vertical ? "x" : "y",
      offset: vertical ? p.x - ends.anchor.x : p.y - ends.anchor.y,
    });
  }
  return channels;
}

/**
 * 直交部分（a1 → b1）を求める。手動経路が成り立てばそれを、成り立たなければ自動経路を使う。
 */
function computeOrthogonalPart(
  ends: RouteEnds,
  route: ConnectorRoute
): {
  readonly points: ConnectorPoint[];
  readonly channels: ConnectorChannel[];
  readonly adjusted: boolean;
  readonly manual: boolean;
} {
  if (route.mode === "manual") {
    const manual = reconstructManualRoute(ends.ortho, route.constraints, ends.obstacles, false);
    if (manual) {
      return {
        points: manual.points,
        channels: manual.channels,
        adjusted: manual.adjusted,
        manual: true,
      };
    }
  }
  const auto = routeOrthogonal(ends.ortho.start, ends.ortho.end, ends.obstacles);
  if (route.mode === "manual" && auto.crossing) {
    // 図形が重なっていて自動経路でも貫通を避けられない配置では、貫通以外の条件だけで手動経路を
    // 確かめる（区間のドラッグ moveConnectorSegment と同じ判定。ドラッグで作った経路を描画で捨てない）。
    const manual = reconstructManualRoute(ends.ortho, route.constraints, ends.obstacles, true);
    if (manual) {
      return {
        points: manual.points,
        channels: manual.channels,
        adjusted: manual.adjusted,
        manual: true,
      };
    }
  }
  const points = normalizePolyline(auto.points) ?? dedupePoints(auto.points);
  return {
    points,
    channels: channelsFromOrthoPoints(ends.ortho, points),
    adjusted: route.mode === "manual",
    manual: false,
  };
}

// --- 端の形状 ---------------------------------------------------------------------

/** 線幅に応じた端の形状の大きさ。 */
export function connectorCapSize(strokeWidth: number): number {
  return Math.max(CONNECTOR_CAP_BASE_PX, 3 * strokeWidth);
}

/**
 * 端の形状の描画要素を求める。`direction` は端点へ向かう単位ベクトル（線の進行方向）。
 * ERD の複合記号は、端点から線の内側へ向かう順に並べる（両端で鏡映になる）。
 */
export function buildConnectorCap(
  cap: ConnectorCap,
  tip: ConnectorPoint,
  direction: ConnectorPoint,
  strokeWidth: number
): ConnectorCapGeometry | null {
  if (cap === "none") return null;
  const size = connectorCapSize(strokeWidth);
  const u = direction;
  // 進行方向に直交する単位ベクトル。
  const v = { x: -u.y, y: u.x };
  const back = (distance: number): ConnectorPoint => ({
    x: tip.x - u.x * distance,
    y: tip.y - u.y * distance,
  });
  const side = (p: ConnectorPoint, offset: number): ConnectorPoint => ({
    x: p.x + v.x * offset,
    y: p.y + v.y * offset,
  });
  const bar = (distance: number): ConnectorPoint[] => [
    side(back(distance), size / 2),
    side(back(distance), -size / 2),
  ];
  const crow = (distance: number): ConnectorPoint[][] => [
    [back(distance), side(tip, size / 2)],
    [back(distance), side(tip, -size / 2)],
  ];
  const base = { cap, tip, direction: u };
  switch (cap) {
    case "arrow-lines": {
      const angle = (26 * Math.PI) / 180;
      const along = size * Math.cos(angle);
      const across = size * Math.sin(angle);
      return {
        ...base,
        strokes: [[side(back(along), across), tip, side(back(along), -across)]],
        fills: [],
        circles: [],
        extent: along,
        trim: 0,
      };
    }
    case "arrow-equilateral": {
      const height = (size * Math.sqrt(3)) / 2;
      return {
        ...base,
        strokes: [],
        fills: [[tip, side(back(height), size / 2), side(back(height), -size / 2)]],
        circles: [],
        extent: height,
        trim: height / 2,
      };
    }
    case "triangle-filled": {
      // 逆向きの塗り三角。底辺を端点に置き、頂点を線の内側へ向ける。
      const height = (size * Math.sqrt(3)) / 2;
      return {
        ...base,
        strokes: [],
        fills: [[side(tip, size / 2), side(tip, -size / 2), back(height)]],
        circles: [],
        extent: height,
        trim: height / 2,
      };
    }
    case "diamond-filled": {
      const length = size * 1.2;
      const width = size * 0.8;
      return {
        ...base,
        strokes: [],
        fills: [
          [
            tip,
            side(back(length / 2), width / 2),
            back(length),
            side(back(length / 2), -width / 2),
          ],
        ],
        circles: [],
        extent: length,
        trim: length / 2,
      };
    }
    case "circle-filled": {
      // 端点を中心に塗りの丸を置く（端点の接続座標は変えない）。
      const radius = size * 0.4;
      return {
        ...base,
        strokes: [],
        fills: [],
        circles: [{ center: tip, radius, filled: true }],
        extent: radius,
        trim: 0,
      };
    }
    case "erd-one":
      return {
        ...base,
        strokes: [bar(size * 0.5)],
        fills: [],
        circles: [],
        extent: size * 0.5,
        trim: 0,
      };
    case "erd-exactly-one":
      return {
        ...base,
        strokes: [bar(size * 0.35), bar(size * 0.7)],
        fills: [],
        circles: [],
        extent: size * 0.7,
        trim: 0,
      };
    case "erd-many":
      return { ...base, strokes: crow(size), fills: [], circles: [], extent: size, trim: 0 };
    case "erd-one-or-more":
      return {
        ...base,
        strokes: [...crow(size), bar(size * 1.3)],
        fills: [],
        circles: [],
        extent: size * 1.3,
        trim: 0,
      };
    case "erd-zero-or-more": {
      // 足（多）の先に中空の丸（0）。丸の内側には線を通さない（線を丸の外側で切り、端点側は形状で描く）。
      const radius = size * 0.3;
      const center = size + size * 0.15 + radius;
      return {
        ...base,
        strokes: [...crow(size), [tip, back(center - radius)]],
        fills: [],
        circles: [{ center: back(center), radius, filled: false }],
        extent: center + radius,
        trim: center + radius,
      };
    }
    case "erd-zero-or-one": {
      // 端点側から 1 の棒、その先に中空の丸（0）。
      const radius = size * 0.3;
      const center = size + radius;
      return {
        ...base,
        strokes: [bar(size * 0.5), [tip, back(center - radius)]],
        fills: [],
        circles: [{ center: back(center), radius, filled: false }],
        extent: center + radius,
        trim: center + radius,
      };
    }
  }
}

// --- 描画命令 ---------------------------------------------------------------------

function unitVector(from: ConnectorPoint, to: ConnectorPoint): ConnectorPoint | null {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (length < EPSILON) return null;
  return { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
}

function distance(p: ConnectorPoint, q: ConnectorPoint): number {
  return Math.hypot(q.x - p.x, q.y - p.y);
}

/** 角の丸めの半径。曲線では隣接区間の半分、折れ線では 10px を上限にし、両端は形状のぶんを残す。 */
function cornerRadii(
  points: readonly ConnectorPoint[],
  lineType: ConnectorLineType,
  reservedStart: number,
  reservedEnd: number,
  obstacles: readonly RouteObstacle[]
): number[] {
  const radii: number[] = [];
  const last = points.length - 1;
  for (let index = 1; index < last; index += 1) {
    const previous = points[index - 1]!;
    const corner = points[index]!;
    const next = points[index + 1]!;
    const inLength = distance(previous, corner);
    const outLength = distance(corner, next);
    let radius = Math.min(inLength / 2, outLength / 2);
    if (index === 1) radius = Math.min(radius, Math.max(0, inLength - reservedStart));
    if (index === last - 1) radius = Math.min(radius, Math.max(0, outLength - reservedEnd));
    if (lineType === "elbowed") {
      radius = Math.min(radius, CONNECTOR_ELBOW_RADIUS_PX);
    } else {
      // 曲線は角を大きく丸めるため、角を切り取る三角形（凸包）が図形の内部へ入らない半径まで縮める
      // （有限回。縮めきっても入る場合は丸めない）。
      let fits = false;
      for (let attempt = 0; attempt < 8 && radius >= 1; attempt += 1) {
        if (!cornerHullHitsObstacles(previous, corner, next, radius, obstacles)) {
          fits = true;
          break;
        }
        radius /= 2;
      }
      if (!fits) radius = 0;
    }
    radii.push(Math.max(0, radius));
  }
  return radii;
}

/** 角の丸め（二次ベジェ）の凸包（始点・角・終点の三角形）が図形の内部へ入るか。 */
function cornerHullHitsObstacles(
  previous: ConnectorPoint,
  corner: ConnectorPoint,
  next: ConnectorPoint,
  radius: number,
  obstacles: readonly RouteObstacle[]
): boolean {
  const inUnit = unitVector(previous, corner);
  const outUnit = unitVector(corner, next);
  if (!inUnit || !outUnit) return false;
  const start = { x: corner.x - inUnit.x * radius, y: corner.y - inUnit.y * radius };
  const end = { x: corner.x + outUnit.x * radius, y: corner.y + outUnit.y * radius };
  for (const obstacle of obstacles) {
    if (segmentHitsFrame(start, end, obstacle.frame, 0)) return true;
    if (pointInsideTriangle(frameCenter(obstacle.frame.frame), start, corner, end)) return true;
  }
  return false;
}

function pointInsideTriangle(
  p: ConnectorPoint,
  a: ConnectorPoint,
  b: ConnectorPoint,
  c: ConnectorPoint
): boolean {
  const sign = (p1: ConnectorPoint, p2: ConnectorPoint, p3: ConnectorPoint): number =>
    (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = sign(p, a, b);
  const d2 = sign(p, b, c);
  const d3 = sign(p, c, a);
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNegative && hasPositive);
}

/**
 * ガイドの折れ線から描画命令を作る。角は半径 radii で丸め（二次ベジェ）、始点・終点は trimStart /
 * trimEnd だけ内側へ縮める（縮めるのは端の直線部分の範囲内）。
 */
function buildPathCommands(
  points: readonly ConnectorPoint[],
  radii: readonly number[],
  trimStart: number,
  trimEnd: number
): ConnectorPathCommand[] {
  if (points.length === 0) return [];
  const first = points[0]!;
  if (points.length === 1) return [{ kind: "M", to: first }];
  const lastIndex = points.length - 1;
  const firstRadius = radii[0] ?? 0;
  const lastRadius = radii[radii.length - 1] ?? 0;
  const firstStraight = Math.max(
    0,
    distance(first, points[1]!) - (points.length > 2 ? firstRadius : 0)
  );
  const last = points[lastIndex]!;
  const lastStraight = Math.max(
    0,
    distance(points[lastIndex - 1]!, last) - (points.length > 2 ? lastRadius : 0)
  );
  const firstUnit = unitVector(first, points[1]!);
  const lastUnit = unitVector(points[lastIndex - 1]!, last);
  const startTrim =
    points.length === 2
      ? Math.min(trimStart, firstStraight / 2)
      : Math.min(trimStart, firstStraight);
  const endTrim =
    points.length === 2 ? Math.min(trimEnd, lastStraight / 2) : Math.min(trimEnd, lastStraight);
  const start = firstUnit
    ? { x: first.x + firstUnit.x * startTrim, y: first.y + firstUnit.y * startTrim }
    : first;
  const end = lastUnit
    ? { x: last.x - lastUnit.x * endTrim, y: last.y - lastUnit.y * endTrim }
    : last;
  const commands: ConnectorPathCommand[] = [{ kind: "M", to: start }];
  for (let index = 1; index < lastIndex; index += 1) {
    const previous = points[index - 1]!;
    const corner = points[index]!;
    const next = points[index + 1]!;
    const radius = radii[index - 1] ?? 0;
    const inUnit = unitVector(previous, corner);
    const outUnit = unitVector(corner, next);
    if (radius < EPSILON || !inUnit || !outUnit) {
      commands.push({ kind: "L", to: corner });
      continue;
    }
    const entry = { x: corner.x - inUnit.x * radius, y: corner.y - inUnit.y * radius };
    const exit = { x: corner.x + outUnit.x * radius, y: corner.y + outUnit.y * radius };
    const lastCommand = commands[commands.length - 1]!;
    if (distance(lastCommand.to, entry) > EPSILON) commands.push({ kind: "L", to: entry });
    commands.push({ kind: "Q", control: corner, to: exit });
  }
  const lastCommand = commands[commands.length - 1]!;
  if (distance(lastCommand.to, end) > EPSILON || commands.length === 1) {
    commands.push({ kind: "L", to: end });
  }
  return commands;
}

/** 描画命令の外接矩形（二次ベジェの極値を含む）。 */
function commandsBounds(commands: readonly ConnectorPathCommand[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const include = (p: ConnectorPoint): void => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };
  let cursor: ConnectorPoint | null = null;
  for (const command of commands) {
    if (command.kind === "Q" && cursor) {
      const p0 = cursor;
      const p1 = command.control;
      const p2 = command.to;
      // 各軸の導関数の根 t = (p0 - p1) / (p0 - 2p1 + p2) が (0, 1) にあれば、その点も含める。
      for (const axis of ["x", "y"] as const) {
        const denominator = p0[axis] - 2 * p1[axis] + p2[axis];
        if (Math.abs(denominator) < 1e-12) continue;
        const t = (p0[axis] - p1[axis]) / denominator;
        if (t <= 0 || t >= 1) continue;
        const mt = 1 - t;
        include({
          x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
          y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
        });
      }
    }
    include(command.to);
    cursor = command.to;
  }
  return { minX, minY, maxX, maxY };
}

/** 端の形状の外接矩形。 */
function capBounds(
  cap: ConnectorCapGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
): void {
  const include = (p: ConnectorPoint): void => {
    if (p.x < bounds.minX) bounds.minX = p.x;
    if (p.y < bounds.minY) bounds.minY = p.y;
    if (p.x > bounds.maxX) bounds.maxX = p.x;
    if (p.y > bounds.maxY) bounds.maxY = p.y;
  };
  for (const stroke of cap.strokes) stroke.forEach(include);
  for (const fill of cap.fills) fill.forEach(include);
  for (const circle of cap.circles) {
    include({ x: circle.center.x - circle.radius, y: circle.center.y - circle.radius });
    include({ x: circle.center.x + circle.radius, y: circle.center.y + circle.radius });
  }
}

/** ガイドの点列から、ハンドルを置ける区間を求める。 */
function buildSegments(
  guide: readonly ConnectorPoint[],
  orthoStartIndex: number,
  orthoEndIndex: number,
  lineType: ConnectorLineType
): ConnectorGuideSegment[] {
  if (lineType === "straight") {
    const start = guide[0];
    const end = guide[guide.length - 1];
    if (!start || !end) return [];
    return [
      {
        index: 0,
        start,
        end,
        axis: null,
        role: "single",
        handlePoint: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 },
      },
    ];
  }
  const segments: ConnectorGuideSegment[] = [];
  const orthoSegmentCount = orthoEndIndex - orthoStartIndex;
  for (let index = orthoStartIndex; index < orthoEndIndex; index += 1) {
    const start = guide[index]!;
    const end = guide[index + 1]!;
    const vertical = Math.abs(start.x - end.x) < EPSILON;
    const horizontal = Math.abs(start.y - end.y) < EPSILON;
    if (!vertical && !horizontal) continue;
    const position = index - orthoStartIndex;
    const role: ConnectorSegmentRole =
      orthoSegmentCount === 1
        ? "single"
        : position === 0
          ? "start"
          : position === orthoSegmentCount - 1
            ? "end"
            : "middle";
    segments.push({
      index,
      start,
      end,
      axis: vertical ? "x" : "y",
      role,
      ...(role === "middle" ? { channelIndex: position - 1 } : {}),
      handlePoint: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 },
    });
  }
  return segments;
}

/**
 * コネクタの経路・描画命令・両端の形状・外接矩形・ハンドルの区間を求める。すべての描画面
 * （キャンバス・作成プレビュー・PDF・サムネイル）と、保存 bbox の計算がこの結果を使う。
 */
export function computeConnectorGeometry(input: ConnectorGeometryInput): ConnectorGeometry {
  const lineType = input.lineType ?? CONNECTOR_DEFAULT_LINE_TYPE;
  const linePattern = input.linePattern ?? CONNECTOR_DEFAULT_LINE_PATTERN;
  const strokeWidth = resolveStrokeWidth(input.strokeWidth);
  const startCapKind = input.startCap ?? CONNECTOR_DEFAULT_START_CAP;
  const endCapKind = input.endCap ?? CONNECTOR_DEFAULT_END_CAP;
  const ends = resolveRouteEnds(input);
  let guide: ConnectorPoint[];
  let orthoStartIndex = 0;
  let orthoEndIndex = 0;
  let channels: ConnectorChannel[] = [];
  let adjusted = false;
  let manual = false;
  if (lineType === "straight") {
    guide = [ends.a, ends.b];
  } else {
    const ortho = computeOrthogonalPart(ends, input.route ?? { mode: "auto" });
    channels = ortho.channels;
    adjusted = ortho.adjusted;
    manual = ortho.manual;
    const head = ends.a1 === ends.a ? [] : [ends.a];
    const tail = ends.b1 === ends.b ? [] : [ends.b];
    guide = [...head, ...ortho.points, ...tail];
    orthoStartIndex = head.length;
    orthoEndIndex = head.length + ortho.points.length - 1;
  }
  // 端の形状の向き（端点へ向かう線の進行方向）。長さ 0 の線では外向き法線の逆向きを使う。
  const startDirection =
    unitVector(guide[1] ?? ends.a, guide[0] ?? ends.a) ??
    negate(sideUnitVector(input.fromSide, input.fromFrame.rotation));
  const endDirection =
    unitVector(guide[guide.length - 2] ?? ends.b, guide[guide.length - 1] ?? ends.b) ??
    negate(sideUnitVector(input.toSide, input.toFrame.rotation));
  const startCap = buildConnectorCap(startCapKind, ends.a, startDirection, strokeWidth);
  const endCap = buildConnectorCap(endCapKind, ends.b, endDirection, strokeWidth);
  const radii =
    lineType === "straight"
      ? []
      : cornerRadii(guide, lineType, startCap?.extent ?? 0, endCap?.extent ?? 0, ends.obstacles);
  const path = buildPathCommands(guide, radii, startCap?.trim ?? 0, endCap?.trim ?? 0);
  const hitPath = buildPathCommands(guide, radii, 0, 0);
  const rawBounds = commandsBounds(hitPath);
  if (startCap) capBounds(startCap, rawBounds);
  if (endCap) capBounds(endCap, rawBounds);
  const pad = strokeWidth / 2;
  const bounds: ConnectorFrame = {
    x: rawBounds.minX - pad,
    y: rawBounds.minY - pad,
    w: rawBounds.maxX - rawBounds.minX + pad * 2,
    h: rawBounds.maxY - rawBounds.minY + pad * 2,
  };
  return {
    lineType,
    linePattern,
    strokeWidth,
    guide,
    path,
    hitPath,
    dashArray: linePattern === "dashed" ? [strokeWidth * 4, strokeWidth * 3] : null,
    startCap,
    endCap,
    segments: dedupeSegmentHandles(buildSegments(guide, orthoStartIndex, orthoEndIndex, lineType)),
    channels,
    bounds,
    adjusted,
    manual,
  };
}

function negate(v: ConnectorPoint): ConnectorPoint {
  return { x: -v.x, y: -v.y };
}

/** 同じ位置（1px 未満）に重なるハンドルは、先に現れた区間の 1 つだけを残す。 */
function dedupeSegmentHandles(segments: readonly ConnectorGuideSegment[]): ConnectorGuideSegment[] {
  const kept: ConnectorGuideSegment[] = [];
  for (const segment of segments) {
    if (kept.some((other) => distance(other.handlePoint, segment.handlePoint) < 1)) continue;
    kept.push(segment);
  }
  return kept;
}

/**
 * 2 つの図形フレームの指定辺どうしを直交折れ線で結ぶルート点列（ステージ座標）を返す。
 * 先頭が from アンカー、末尾が to アンカー。連続する重複点は除去する。
 * 回転がある場合はアンカーを回転後の実位置に、外向き方向を最寄り軸へ吸着して算出する。
 * 経路の改善後は、既定の見た目（角丸の折れ線・自動経路）で結ぶガイドの点列を返し（computeConnectorGeometry の
 * guide と同じ）、一直線上の中間点も除く。回転図形では辺の法線方向の斜めの助走を含む。
 */
export function computeConnectorRoute(
  fromFrame: ConnectorFrame,
  fromSide: ConnectorSide,
  toFrame: ConnectorFrame,
  toSide: ConnectorSide
): ConnectorPoint[] {
  return [...computeConnectorGeometry({ fromFrame, fromSide, toFrame, toSide }).guide];
}

/**
 * 連続する（ほぼ）同一座標の点を 1 つに畳む。
 */
export function dedupePoints(points: readonly ConnectorPoint[]): ConnectorPoint[] {
  const result: ConnectorPoint[] = [];
  for (const point of points) {
    const prev = result[result.length - 1];
    if (prev && Math.abs(prev.x - point.x) < EPSILON && Math.abs(prev.y - point.y) < EPSILON) {
      continue;
    }
    result.push(point);
  }
  return result;
}

/**
 * 点列の外接矩形を返す。描画キャンバスのサイズ算出や保存 bbox の素として使う。
 */
export function routeBoundingBox(points: readonly ConnectorPoint[]): ConnectorFrame {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** 保存枠の計算に使う見た目（未指定は既定値）。 */
export type ConnectorStoredFrameAppearance = Pick<
  ConnectorGeometryInput,
  "lineType" | "route" | "strokeWidth" | "startCap" | "endCap"
>;

/**
 * コネクタの保存用フレーム（x/y/w/h）を返す。ルート外接矩形に矢じり・線幅ぶんの余白を加える。
 * 作成時・端点移動時の保存値を一元化し、AABB 参照箇所で見た目とずれにくくする。
 * 経路の編集の追加後は、ガイドの外接矩形に CONNECTOR_BBOX_PAD_PX の余白を加えたものと、線幅・両端の形状・
 * 曲線のふくらみを含む描画の外接矩形の両方を包含する（経路や形状の更新時の保存値も同じ関数で求める）。
 */
export function connectorStoredFrame(
  fromFrame: ConnectorFrame,
  fromSide: ConnectorSide,
  toFrame: ConnectorFrame,
  toSide: ConnectorSide,
  appearance?: ConnectorStoredFrameAppearance | undefined
): ConnectorFrame {
  const geometry = computeConnectorGeometry({
    fromFrame,
    fromSide,
    toFrame,
    toSide,
    ...(appearance ?? {}),
  });
  const guideBox = routeBoundingBox(geometry.guide);
  const minX = Math.min(guideBox.x - CONNECTOR_BBOX_PAD_PX, geometry.bounds.x);
  const minY = Math.min(guideBox.y - CONNECTOR_BBOX_PAD_PX, geometry.bounds.y);
  const maxX = Math.max(
    guideBox.x + guideBox.w + CONNECTOR_BBOX_PAD_PX,
    geometry.bounds.x + geometry.bounds.w
  );
  const maxY = Math.max(
    guideBox.y + guideBox.h + CONNECTOR_BBOX_PAD_PX,
    geometry.bounds.y + geometry.bounds.h
  );
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// --- 経路編集（区間のドラッグ） ---------------------------------------------------------

/** 区間のドラッグの結果。 */
export interface ConnectorSegmentMoveResult {
  /** 保存するチャネル（id 付き・正規形）。 */
  readonly constraints: readonly ConnectorRouteConstraint[];
  /** 実際に採用した区間の位置（有効な範囲へ寄せた後の値。ステージ座標）。 */
  readonly value: number;
}

/**
 * 表示中の経路の区間 `segmentIndex`（geometry.segments の添字）を、その区間の法線方向の位置 `value`
 * （縦の区間なら x、横の区間なら y。ステージ座標）へ動かしたときの手動経路を返す。
 *
 * - 中間の区間はそのチャネルの位置を変える。
 * - 端点直近の区間（start / end）は、接続点を保ったまま動かせるよう、助走の先で折れる 2 本の
 *   チャネル（dogleg）を補う。両端が一直線に並ぶ区間（single）は両端に補う。
 * - 図形の内部へ入る・助走が足りなくなる位置は、元の位置から `value` へ向かう途中の最も遠い
 *   有効な位置へ寄せる（急に自動経路へ戻さない）。
 * - チャネルが上限（{@link CONNECTOR_ROUTE_CONSTRAINTS_MAX}）を超える場合や、区間を動かせない場合は null。
 *
 * `createId` は新しいチャネルの id を作る（保存済みのチャネルは元の id を保つ）。
 */
export function moveConnectorSegment(params: {
  readonly input: ConnectorGeometryInput;
  readonly geometry: ConnectorGeometry;
  readonly segmentIndex: number;
  readonly value: number;
  readonly createId: () => string;
}): ConnectorSegmentMoveResult | null {
  const { geometry, input } = params;
  const segment = geometry.segments[params.segmentIndex];
  if (!segment || segment.axis === null) return null;
  // 端の区間は折れを補う（start / end は 2 本、single は 3 本）。補うとチャネルが上限を超える場合は
  // 動かせない（元の位置へ寄せた見かけの結果を返さず、呼び出し側が理由を伝えられるよう null にする）。
  const added = segment.role === "middle" ? 0 : segment.role === "single" ? 3 : 2;
  if (geometry.channels.length + added > CONNECTOR_ROUTE_CONSTRAINTS_MAX) return null;
  const ends = resolveRouteEnds({ ...input, lineType: "elbowed" });
  const original = segment.axis === "x" ? segment.start.x : segment.start.y;
  const baseChannels: ConnectorChannel[] = geometry.channels.map((channel) => ({
    axis: channel.axis,
    offset: channel.offset,
    id: channel.id ?? params.createId(),
  }));
  const build = (value: number): RouteLine[] | null => {
    const lines = buildMovedLines(ends.ortho, baseChannels, segment, value, params.createId);
    if (!lines) return null;
    return simplifyLines(lines);
  };
  // 図形が重なっていて自動経路でも貫通を避けられない配置では、貫通以外の条件だけで判定する
  // （判定できずに区間を一切動かせなくなるのを防ぐ）。描画（computeOrthogonalPart）と同じ判定にし、
  // ドラッグで作った経路を描画で捨てないようにする。
  const ignoreCrossings = routeOrthogonal(ends.ortho.start, ends.ortho.end, ends.obstacles).crossing;
  const isValid = (lines: readonly RouteLine[] | null): boolean =>
    lines !== null &&
    lines.filter((line) => !line.fixed).length <= CONNECTOR_ROUTE_CONSTRAINTS_MAX &&
    isValidLineRoute(ends.ortho, lines, ends.obstacles, ignoreCrossings);
  // 新しいチャネルの id は呼び出しごとに作られるため、採用した位置で作り直した結果を返す。
  let chosen = params.value;
  if (!isValid(build(chosen))) {
    // 元の位置から目標へ向かう途中の、最も遠い有効な位置を二分探索で求める。元の位置そのものは、
    // 直線の区間（single）では簡約で 1 本の線になり経路の判定に掛からないため、有効とみなして始める
    // （途中に有効な位置が無ければ下の最終確認で null になる）。
    let low = 0;
    let high = 1;
    for (let iteration = 0; iteration < 20; iteration += 1) {
      const middle = (low + high) / 2;
      if (isValid(build(original + (params.value - original) * middle))) low = middle;
      else high = middle;
    }
    chosen = original + (params.value - original) * low;
  }
  const lines = build(chosen);
  if (!lines || !isValid(lines)) return null;
  const constraints: ConnectorRouteConstraint[] = channelsFromLines(ends.ortho, lines).map(
    (channel) => ({
      id: channel.id ?? params.createId(),
      axis: channel.axis,
      offset: channel.offset,
    })
  );
  return { constraints, value: chosen };
}

/** 区間を value へ動かしたときの直線の並び（簡約前）。 */
function buildMovedLines(
  ends: OrthoEnds,
  channels: readonly ConnectorChannel[],
  segment: ConnectorGuideSegment,
  value: number,
  createId: () => string
): RouteLine[] | null {
  const { lines } = linesFromChannels(ends, channels);
  const startLine = lines[0]!;
  const endLine = lines[lines.length - 1]!;
  const startVector = DIR_VECTORS[ends.start.dir]!;
  const endVector = DIR_VECTORS[ends.end.dir]!;
  const middle = lines.slice(1, -1).map((line) => ({ ...line }));
  const segmentHorizontal = segment.axis === "y";
  // 助走の先で折れる位置（相手の折れ点との間を上限に、目安の助走の長さ）。両端に補う single では、
  // 2 つの折れが重ならないよう間の 1/3 までにする。ただし最小の助走（minStub）は必ず確保する
  // （短い区間でも折れを補って動かせるようにする）。
  const doglegCoord = (
    point: ConnectorPoint,
    vector: ConnectorPoint,
    towards: ConnectorPoint | undefined,
    horizontalLine: boolean,
    minStub: number
  ): number => {
    const available = towards
      ? Math.abs(horizontalLine ? towards.x - point.x : towards.y - point.y)
      : Infinity;
    const share = segment.role === "single" ? 3 : 2;
    const stub = Math.max(minStub, Math.min(CONNECTOR_STUB_PX, available / share));
    return horizontalLine ? point.x + vector.x * stub : point.y + vector.y * stub;
  };
  const turns = intersectLines(lines);
  if (segment.role === "single") {
    // 両端に補う 2 つの折れは、始点側の折れが終点側の折れより始点寄りに無いと、重なるか交差し、線が
    // 自分に重なって戻る経路になる（経路の判定は折れどうしの前後を確かめない）。向かい合う端点の間が
    // 狭く、最小の助走を確保すると前後が入れ替わる区間は、折れを置けない区間として動かさない。
    const startDogleg = doglegCoord(
      ends.start.point,
      startVector,
      ends.end.point,
      startLine.horizontal,
      ends.start.minStub
    );
    const endDogleg = doglegCoord(
      ends.end.point,
      endVector,
      ends.start.point,
      endLine.horizontal,
      ends.end.minStub
    );
    const startCoord = startLine.horizontal ? ends.start.point.x : ends.start.point.y;
    const endCoord = startLine.horizontal ? ends.end.point.x : ends.end.point.y;
    const forward = Math.sign(endCoord - startCoord);
    if (forward === 0 || (endDogleg - startDogleg) * forward <= 0) return null;
  }
  if (segment.role === "middle") {
    const target = middle[segment.channelIndex ?? -1];
    if (!target || target.horizontal !== segmentHorizontal) return null;
    middle[segment.channelIndex!] = { ...target, coord: value };
    return [startLine, ...middle, endLine];
  }
  const prepend: RouteLine[] = [];
  const append: RouteLine[] = [];
  if (segment.role === "start" || segment.role === "single") {
    if (startLine.horizontal !== segmentHorizontal) return null;
    const towards = segment.role === "single" ? ends.end.point : turns[0];
    prepend.push(
      {
        horizontal: !startLine.horizontal,
        coord: doglegCoord(
          ends.start.point,
          startVector,
          towards,
          startLine.horizontal,
          ends.start.minStub
        ),
        id: createId(),
        fixed: false,
      },
      { horizontal: startLine.horizontal, coord: value, id: createId(), fixed: false }
    );
  }
  if (segment.role === "end" || segment.role === "single") {
    if (endLine.horizontal !== segmentHorizontal) return null;
    const towards = segment.role === "single" ? ends.start.point : turns[turns.length - 1];
    if (segment.role === "end") {
      append.push({ horizontal: endLine.horizontal, coord: value, id: createId(), fixed: false });
    }
    append.push({
      horizontal: !endLine.horizontal,
      coord: doglegCoord(ends.end.point, endVector, towards, endLine.horizontal, ends.end.minStub),
      id: createId(),
      fixed: false,
    });
  }
  return [startLine, ...prepend, ...middle, ...append, endLine];
}
